import * as echarts from "echarts";
import React, { useEffect, useMemo, useRef, useState } from "react";
import moment from "moment-timezone";
import { DASHBOARD_COMPACT_ECHARTS_GRID } from "src/helpers/chart-helpers";
import type { VolumeCompareSnapshot } from "./volumeCompareChart.types";
import {
  buildVolumeCompareBarGroups,
  buildVolumeCompareTooltipHtml,
  formatVolumeAxis,
  formatVolumeMultiple,
  getAvgBarColor,
  getSelectedVolumeLabel,
  getTodayBaseBarColor,
  hasVolumeCompareData,
} from "./volumeCompareChart.utils";

type ZrLegendItem = {
  __legendDataIndex?: number;
  getBoundingRect(): { x: number; y: number; width: number; height: number };
  transformCoordToGlobal(x: number, y: number): [number, number];
};

type LegendModel = Record<string, unknown>;

type LegendView = {
  getContentGroup(): {
    eachChild(cb: (child: ZrLegendItem) => void): void;
  };
};

type ChartInternals = {
  getModel():
    | {
        getComponent(type: string, index: number): LegendModel | undefined;
      }
    | undefined;
  getViewOfComponentModel(model: LegendModel): LegendView | undefined;
};

type VolumeCompareBarChartProps = {
  snapshot: VolumeCompareSnapshot | null;
  darkMode: boolean;
  isDashboard?: boolean;
  isExpand?: boolean;
  symbolName?: string;
  dateOptions?: string[];
  selectedDate?: Date | null;
  onDateChange?: (date: Date) => void;
};

const DateDropdown = ({
  options,
  value,
  onChange,
  darkMode,
}: {
  options: string[];
  value: string;
  onChange: (val: string) => void;
  darkMode: boolean;
}) => {
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  if (!options || options.length === 0) return null;

  return (
    <div className="relative" ref={menuRef}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={`flex h-4 items-center gap-1 rounded border px-1.5 py-0 text-[10px] font-semibold leading-4 transition-colors cursor-pointer ${
          darkMode
            ? "border-gray-600/70 bg-[#252530]/90 text-gray-200 hover:border-gray-500"
            : "border-gray-300/80 bg-white/90 text-gray-700 hover:border-gray-400"
        }`}
      >
        {value ? moment(value).format("DD MMM") : "Select"} ▾
      </button>
      {open && (
        <ul
          className={`absolute right-0 top-full z-20 mt-1 max-h-40 overflow-y-auto rounded border py-1 shadow-lg w-max min-w-[5rem] ${
            darkMode
              ? "border-gray-600/70 bg-[#252530] text-gray-200"
              : "border-gray-300/80 bg-white text-gray-700"
          }`}
        >
          {options.map((dateStr) => (
            <li key={dateStr}>
              <button
                type="button"
                className={`block w-full cursor-pointer px-3 py-1.5 text-left text-[10px] font-medium transition-colors ${
                  dateStr === value
                    ? darkMode
                      ? "bg-cyan-900/40 text-cyan-200"
                      : "bg-teal-50 text-teal-800"
                    : darkMode
                      ? "hover:bg-gray-700/80"
                      : "hover:bg-gray-100"
                }`}
                onClick={() => {
                  onChange(dateStr);
                  setOpen(false);
                }}
              >
                {moment(dateStr).format("DD MMM")}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const VolumeCompareBarChart = ({
  snapshot,
  darkMode,
  isDashboard,
  isExpand,
  dateOptions,
  selectedDate,
  onDateChange,
}: VolumeCompareBarChartProps) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstance = useRef<echarts.EChartsType | null>(null);
  const [dropdownLeft, setDropdownLeft] = useState<number | null>(null);
  const [dropdownTop, setDropdownTop] = useState<number | null>(null);

  const groups = useMemo(
    () => buildVolumeCompareBarGroups(snapshot),
    [snapshot],
  );

  const selectedVolumeLabel = useMemo(
    () => (snapshot ? getSelectedVolumeLabel(snapshot) : "Volume as on Today"),
    [snapshot],
  );

  const categories = useMemo(
    () => groups.map((g) => (isDashboard || isExpand ? g.shortLabel : g.label)),
    [groups, isDashboard, isExpand],
  );
  const avgValues = useMemo(() => groups.map((g) => g.avgVolume), [groups]);
  const todayValues = useMemo(() => groups.map((g) => g.todayVolume), [groups]);

  const avgColor = getAvgBarColor(darkMode);
  const todayColor = getTodayBaseBarColor(darkMode);

  const barMaxWidth = isExpand ? 36 : isDashboard ? 24 : 30;
  const barCategoryGap = isDashboard ? "18%" : "22%";

  const axisColor = darkMode ? "#94a3b8" : "#64748b";

  const volumeYMax = useMemo(() => {
    const peak = Math.max(...avgValues, ...todayValues, 0);
    if (peak <= 0) return 1;
    const headroomFactor = isDashboard ? 1.35 : isExpand ? 1.2 : 1.25;
    return peak * headroomFactor;
  }, [avgValues, todayValues, isDashboard, isExpand]);

  useEffect(() => {
    return () => {
      chartInstance.current?.dispose();
      chartInstance.current = null;
    };
  }, []);

  useEffect(() => {
    if (!chartRef.current) return;

    if (!chartInstance.current) {
      chartInstance.current = echarts.init(
        chartRef.current,
        darkMode ? "dark" : "light",
      );

      chartInstance.current.on("finished", () => {
        const chart = chartInstance.current;
        if (!chart) return;
        const chartInternals = chart as unknown as ChartInternals;
        const model = chartInternals.getModel();
        if (!model) return;
        const legendModel = model.getComponent("legend", 0);
        if (!legendModel) return;

        const legendView = chartInternals.getViewOfComponentModel(legendModel);
        const contentGroup = legendView?.getContentGroup();
        if (!contentGroup) return;

        const legendItems: ZrLegendItem[] = [];
        contentGroup.eachChild((child) => {
          if (child.__legendDataIndex !== undefined) {
            legendItems.push(child);
          }
        });

        // Target the last legend item (index 1)
        const targetItem = legendItems.find(
          (child) => child.__legendDataIndex === 1,
        );
        if (!targetItem) return;

        const rect = targetItem.getBoundingRect();
        const [rightX, centerY] = targetItem.transformCoordToGlobal(
          rect.x + rect.width,
          rect.y + rect.height / 2,
        );

        setDropdownLeft(rightX + 12);
        setDropdownTop(centerY - 8); // Assuming dropdown is ~16px tall (h-4)
      });
    }

    const multipleLabelColor = darkMode ? "#e2e8f0" : "#1e293b";
    const multipleLabelSize = isExpand ? 13 : isDashboard ? 10 : 11;

    const barStyle = { barMaxWidth, barCategoryGap };

    const option: echarts.EChartsOption = {
      backgroundColor: "transparent",
      emphasis: { focus: "none", blurScope: "none" },
      animation: true,
      animationDuration: 600,
      animationEasing: "cubicOut",
      legend: {
        show: true,
        left: "center",
        top: isDashboard ? 4 : undefined,
        type: isDashboard ? "scroll" : "plain",
        textStyle: { color: axisColor, fontSize: isDashboard ? 9 : 10 },
        data: [
          { name: "Avg volume", itemStyle: { color: avgColor } },
          {
            name: selectedVolumeLabel,
            itemStyle: { color: todayColor },
          },
        ],
        itemWidth: isDashboard ? 8 : 10,
        itemHeight: isDashboard ? 6 : 8,
        itemGap: isDashboard ? 6 : 10,
      },
      grid: isDashboard
        ? { ...DASHBOARD_COMPACT_ECHARTS_GRID, top: 44 }
        : {
            top: isExpand ? 36 : 32,
            left: 2,
            right: 8,
            bottom: isExpand ? 36 : 30,
            containLabel: true,
          },
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "shadow" },
        confine: true,
        backgroundColor: darkMode ? "#1e293b" : "#ffffff",
        borderColor: darkMode ? "#334155" : "#e2e8f0",
        textStyle: { color: darkMode ? "#e2e8f0" : "#1e293b" },
        formatter: (params) => {
          const items = Array.isArray(params) ? params : [params];
          const idx = items[0]?.dataIndex;
          if (idx == null || !groups[idx] || !snapshot) return "";
          return buildVolumeCompareTooltipHtml(
            groups[idx],
            snapshot,
            darkMode,
            selectedVolumeLabel,
          );
        },
      },
      xAxis: {
        show: hasVolumeCompareData(snapshot),
        type: "category",
        data: categories,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
          color: axisColor,
          fontSize: isExpand ? 12 : isDashboard ? 9 : 10,
          fontWeight: 500,
          interval: 0,
          margin: isDashboard ? 6 : 8,
        },
      },
      yAxis: {
        show: hasVolumeCompareData(snapshot),
        type: "value",
        min: 0,
        max: volumeYMax,
        splitLine: { show: false },
        axisLabel: {
          color: axisColor,
          fontSize: isExpand ? 11 : 10,
          formatter: (v: number) => formatVolumeAxis(v),
        },
      },
      series: [
        {
          name: "Avg volume",
          type: "bar",
          ...barStyle,
          barGap: "20%",
          z: 1,
          itemStyle: {
            borderRadius: [4, 4, 0, 0],
            color: avgColor,
            opacity: darkMode ? 0.9 : 1,
          },
          data: avgValues,
        },
        {
          name: selectedVolumeLabel,
          type: "bar",
          ...barStyle,
          z: 2,
          itemStyle: {
            borderRadius: [4, 4, 0, 0],
            color: todayColor,
            borderColor: darkMode ? "rgba(255, 255, 255, 0.35)" : "#cbd5e1",
            borderWidth: 1,
            opacity: 1,
          },
          label: {
            show: hasVolumeCompareData(snapshot),
            position: "top",
            distance: 6,
            fontSize: multipleLabelSize,
            fontWeight: 700,
            color: multipleLabelColor,
            verticalAlign: "bottom",
            formatter: (params) => {
              const idx =
                typeof params === "object" && params && "dataIndex" in params
                  ? (params.dataIndex as number)
                  : -1;
              const multiple = groups[idx]?.volumeMultiple ?? 0;
              if (!Number.isFinite(multiple) || multiple <= 0) return "";
              return formatVolumeMultiple(multiple);
            },
          },
          data: todayValues,
        },
      ],
    };

    chartInstance.current.setOption(option, false);

    let resizeTimer: ReturnType<typeof setTimeout>;
    let width = chartRef.current?.clientWidth;
    let height = chartRef.current?.clientHeight;

    const observer = new ResizeObserver(() => {
      if (!chartRef.current) return;
      const newWidth = chartRef.current.clientWidth;
      const newHeight = chartRef.current.clientHeight;

      if (newWidth === width && newHeight === height) return;

      width = newWidth;
      height = newHeight;

      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        chartInstance.current?.resize({
          animation: { duration: 300, easing: "cubicOut" },
        });
      }, 100);
    });
    if (chartRef.current) {
      observer.observe(chartRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [
    snapshot,
    groups,
    categories,
    avgValues,
    todayValues,
    avgColor,
    todayColor,
    darkMode,
    isExpand,
    isDashboard,
    barMaxWidth,
    barCategoryGap,
    axisColor,
    volumeYMax,
    selectedVolumeLabel,
  ]);

  return (
    <div className="h-full min-h-0 w-full relative">
      <div
        className={`absolute z-10 transition-opacity duration-200 ${dropdownLeft === null ? "opacity-0 pointer-events-none" : "opacity-100"}`}
        style={{ left: dropdownLeft ?? 0, top: dropdownTop ?? 0 }}
      >
        <DateDropdown
          options={dateOptions ?? []}
          value={
            selectedDate
              ? moment.tz(selectedDate, "Asia/Kolkata").format("YYYY-MM-DD")
              : ""
          }
          onChange={(val) =>
            onDateChange?.(moment.tz(val, "Asia/Kolkata").toDate())
          }
          darkMode={darkMode}
        />
      </div>
      <div
        ref={chartRef}
        id="volume-compare-bar-chart"
        className={`h-full w-full ${isExpand ? "min-h-[360px]" : isDashboard ? "min-h-0" : "min-h-[240px]"}`}
      />
      {!hasVolumeCompareData(snapshot) && (
        <div className="absolute inset-0 top-12 flex items-center justify-center pointer-events-none text-xs text-gray-500 dark:text-gray-400">
          No volume data found for this date
        </div>
      )}
    </div>
  );
};

export default React.memo(VolumeCompareBarChart);
